// A thin wrapper around Stockfish compiled to WebAssembly, running in a Web Worker in the browser.
// It grades moves the player makes on the board (repairs, explored lines) against the position on
// screen; the server engine has already scored the game's own moves. `playMove` also picks moves as a
// deliberately weakened opponent for "play it out from here" (see PlayOutBoard).
//
// The engine is the `lite single-threaded` Stockfish 18 build (nmrugg/stockfish.js): ~7 MB, no
// SharedArrayBuffer or COOP/COEP headers needed. copy-webpack-plugin puts the loader and .wasm in
// wwwroot/js/dist/stockfish/; they must sit together.

import { clientLog } from './clientLog';

// Copied here by webpack (see webpack.js).
const ENGINE_URL = '/js/dist/stockfish/stockfish-18-lite-single.js';

// Evaluations are serialised through one chain, so a call that never settles would block every later
// call. These bound the two waits; on timeout the worker is discarded and the next call rebuilds it.
// Generous because the 7 MB wasm can be slow on mobile.
const LOAD_TIMEOUT_MS = 30000;
const EVAL_TIMEOUT_MS = 15000;

// One position's evaluation, White-relative like the rest of the client (positive = White better).
// Exactly one of scoreCp / mateIn is non-null, except checkmate on the board ("mate 0"), which
// carries mateIn 0 and a signed ±1000 scoreCp: negating 0 can't flip a sign, so the winner comes from
// the FEN (the side to move is mated). Read a win chance with whiteWinPct(e.scoreCp, e.mateIn);
// testing `mateIn > 0` alone gets mate 0 wrong. Twin of StockfishEvaluation.MateScoreCp.
export type EngineEval = {
  scoreCp: number | null; // white-relative centipawns
  mateIn: number | null; // white-relative mate distance in moves (+ = White mates, 0 = mated now)
  bestMove: string | null; // best reply from this position, UCI
  pv: string[]; // principal variation from this position (UCI moves), best line first; may be empty
  depth: number;
};

// `Skill Level` is a persistent UCI option and both callers share one worker, so every `go` states
// the strength it wants and the option is set only when it changes.
const FULL_SKILL = 20;

class StockfishEngine {
  private worker: Worker | null = null;
  private ready: Promise<void> | null = null;
  private listeners: ((line: string) => void)[] = [];
  // The engine handles one `go` at a time, so calls chain after each other.
  private chain: Promise<unknown> = Promise.resolve();
  // The `Skill Level` currently set on the worker; null when unset (a fresh engine is at its default).
  private appliedSkill: number | null = null;

  // Guard so an unsupported browser hides the feature rather than throwing.
  isSupported(): boolean {
    return typeof Worker !== 'undefined' && typeof WebAssembly !== 'undefined';
  }

  // Tear the engine down so the next call rebuilds it, so a wedged worker never blocks the chain.
  private reset(): void {
    if (this.worker) {
      try {
        this.worker.terminate();
      } catch {
        /* already gone */
      }
    }
    this.worker = null;
    this.ready = null;
    this.listeners = [];
    this.appliedSkill = null;
  }

  // Set `Skill Level` if needed. Sent directly, not through the chain, because callers run it inside
  // the slot they already hold.
  private applySkill(skill: number): void {
    if (this.appliedSkill === skill) return;
    this.worker!.postMessage(`setoption name Skill Level value ${skill}`);
    this.appliedSkill = skill;
  }

  // Lazily start the worker and run the UCI handshake, at most once and only when needed.
  private ensureReady(): Promise<void> {
    if (this.ready) {
      return this.ready;
    }
    this.ready = new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        clientLog.warn('Stockfish load timed out', { timeoutMs: LOAD_TIMEOUT_MS });
        this.reset();
        reject(new Error('Stockfish load timed out'));
      }, LOAD_TIMEOUT_MS);
      const fail = (err: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.reset();
        reject(err);
      };
      const succeed = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      try {
        const w = new Worker(
          typeof window !== 'undefined' ? new URL(ENGINE_URL, window.location.origin).href : ENGINE_URL,
        );
        this.worker = w;
        w.onmessage = (e: MessageEvent) => {
          const line = typeof e.data === 'string' ? e.data : String((e.data && e.data.data) ?? '');
          this.listeners.forEach((l) => l(line));
        };
        w.onerror = () => {
          clientLog.error('Stockfish worker error during load');
          fail(new Error('Stockfish failed to load'));
        };
        const onLine = (line: string) => {
          if (line === 'uciok') {
            w.postMessage('isready');
          } else if (line === 'readyok') {
            this.listeners = this.listeners.filter((l) => l !== onLine);
            succeed();
          }
        };
        this.listeners.push(onLine);
        w.postMessage('uci');
      } catch (err) {
        fail(err instanceof Error ? err : new Error('Stockfish failed to load'));
      }
    });
    return this.ready;
  }

  // Warm the engine up front so the first evaluation doesn't pay the load. Safe to call repeatedly.
  preload(): Promise<void> {
    return this.ensureReady();
  }

  // Evaluate a FEN to a fixed depth. Depth 15 matches the server's, so scores share a scale.
  evaluate(fen: string, opts: { depth?: number } = {}): Promise<EngineEval> {
    const run = () => this.runEvaluate(fen, opts.depth ?? 15);
    const next = this.chain.then(run, run);
    // Keep the chain alive even if one evaluation rejects.
    this.chain = next.catch(() => undefined);
    return next;
  }

  // Pick a move as a weakened opponent: `skill` is Stockfish's 0–20 Skill Level, `depth` shortens the
  // search further. Null when there is no move (mate/stalemate). Shares the serial chain.
  playMove(fen: string, opts: { skill: number; depth: number }): Promise<string | null> {
    const run = () => this.runPlay(fen, opts.skill, opts.depth);
    const next = this.chain.then(run, run);
    this.chain = next.catch(() => undefined);
    return next;
  }

  private async runPlay(fen: string, skill: number, depth: number): Promise<string | null> {
    await this.ensureReady();
    const w = this.worker!;
    return new Promise<string | null>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.listeners = this.listeners.filter((l) => l !== onLine);
        clientLog.warn('Stockfish play timed out', {
          fen,
          skill,
          depth,
          timeoutMs: EVAL_TIMEOUT_MS,
        });
        this.reset();
        reject(new Error('Stockfish play timed out'));
      }, EVAL_TIMEOUT_MS);
      const onLine = (line: string) => {
        if (!line.startsWith('bestmove')) return;
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.listeners = this.listeners.filter((l) => l !== onLine);
        const bm = /^bestmove (\S+)/.exec(line);
        resolve(bm && bm[1] !== '(none)' ? bm[1] : null);
      };
      this.listeners.push(onLine);
      this.applySkill(Math.max(0, Math.min(FULL_SKILL, skill)));
      w.postMessage('position fen ' + fen);
      w.postMessage('go depth ' + depth);
    });
  }

  private async runEvaluate(fen: string, depth: number): Promise<EngineEval> {
    await this.ensureReady();
    const w = this.worker!;
    // UCI scores are from the side to move; flip to White-relative when Black moves.
    const whiteToMove = fen.split(' ')[1] !== 'b';
    return new Promise<EngineEval>((resolve, reject) => {
      let scoreCp: number | null = null;
      let mateIn: number | null = null;
      let seenDepth = 0;
      let pv: string[] = [];
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.listeners = this.listeners.filter((l) => l !== onLine);
        clientLog.warn('Stockfish evaluate timed out', {
          fen,
          depth,
          seenDepth,
          timeoutMs: EVAL_TIMEOUT_MS,
        });
        // Discard the wedged worker so the next evaluation rebuilds.
        this.reset();
        reject(new Error('Stockfish evaluate timed out'));
      }, EVAL_TIMEOUT_MS);
      const onLine = (line: string) => {
        if (line.startsWith('info') && line.indexOf(' score ') !== -1) {
          const dm = /\bdepth (\d+)/.exec(line);
          if (dm) {
            seenDepth = parseInt(dm[1], 10);
          }
          const cp = /\bscore cp (-?\d+)/.exec(line);
          const mate = /\bscore mate (-?\d+)/.exec(line);
          if (cp) {
            scoreCp = parseInt(cp[1], 10);
            mateIn = null;
          } else if (mate) {
            mateIn = parseInt(mate[1], 10);
            scoreCp = null;
          }
          // Keep the latest (deepest) principal variation from the info line ("… pv e2e4 e7e5 g1f3").
          const pvm = / pv (.+)$/.exec(line);
          if (pvm) {
            pv = pvm[1].trim().split(/\s+/).filter(Boolean);
          }
        } else if (line.startsWith('bestmove')) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          const bm = /^bestmove (\S+)/.exec(line);
          const bestMove = bm && bm[1] !== '(none)' ? bm[1] : null;
          this.listeners = this.listeners.filter((l) => l !== onLine);
          resolve({
            // Mate on the board ("mate 0") has no sign: the side to move was mated, so write ±1000 accordingly
            // (see the type comment above).
            scoreCp:
              mateIn === 0
                ? whiteToMove
                  ? -1000
                  : 1000
                : scoreCp == null
                  ? null
                  : whiteToMove
                    ? scoreCp
                    : -scoreCp,
            mateIn: mateIn == null ? null : whiteToMove ? mateIn : -mateIn,
            bestMove,
            pv,
            depth: seenDepth,
          });
        }
      };
      this.listeners.push(onLine);
      // Grading always reads at full strength.
      this.applySkill(FULL_SKILL);
      w.postMessage('position fen ' + fen);
      w.postMessage('go depth ' + depth);
    });
  }
}

// One shared engine for the whole app.
export const stockfishEngine = new StockfishEngine();
